<template>
  <vx-card>
    <form-wizard
      ref="wizard"
      class="wizard-no-title gp-sticky-wizard"
      color="rgba(var(--vs-primary), 1)"
      errorColor="rgba(var(--vs-danger), 1)"
      transition="zoom-fade"
      :title="null"
      :subtitle="null"
      :start-index="0"
      @on-change="onChangeTab">

      <tab-content
        :title="$t('$Step1.Title')"
        class="mb-5 pt-4"
        icon="fas fa-check"
        :before-change="async () => await validateAccountDetailsStep()"
      >
        <user-account-details-company-information
          ref="accountDetails"
          as-wizard-step/>
      </tab-content>

      <tab-content
        :title="$t('$Step2.Title')"
        class="mb-5 pt-4"
        icon="far fa-comments"
        :before-change="async () => await validateTemplatesStep()"
      >
        <waiting-list-configuration-template
          ref="configurationTemplate"
          :key="configTemplatesKey"
          :tab-index.sync="configTemplatesTabIndex"
        />
      </tab-content>

      <tab-content
        :title="$t('$Step3.Title')"
        class="mb-5 pt-4"
        icon="far fa-flag"
        :before-change="async () => await validateAccountBrandingStep()"
      >
        <vx-card no-shadow card-border>
          <template #no-body>
            <user-account-branding
              ref="accountBranding"
              as-wizard-step/>
          </template>
        </vx-card>
      </tab-content>

      <tab-content
        :title="$t('$Step4.Title')"
        class="mb-5 pt-4"
        icon="far fa-file"
        :before-change="async () => await validateAccountCompanyPoliciesStep()"
      >
        <user-account-company-policies
          ref="accountCompanyPolicies"
          as-wizard-step/>
      </tab-content>

      <tab-content
        :title="$t('$Step5.Title')"
        class="mb-5 pt-4"
        icon="far fa-calendar"
        :before-change="async () => await validateAccountOpeningHoursStep()"
      >
        <user-account-opening-hours
          ref="accountOpeningHours"
          as-wizard-step/>
      </tab-content>

    </form-wizard>

    <!-- Sticky action bar (shared list-page element) -->
    <wizard-buttons
      :wizard-props="stickyWizardProps"
      :config-templates-tab-index="configTemplatesTabIndex"
      @cancel="$emit('cancel')"
      @save="saveAndClose"
      @next-config-temp-step="configTemplatesTabIndex = configTemplatesTabIndex + 1"
      @prev-config-temp-step="configTemplatesTabIndex = configTemplatesTabIndex - 1"
    />
  </vx-card>
</template>

<script>
import { mapGetters } from 'vuex';
import { FormWizard, TabContent } from 'vue-form-wizard';
import 'vue-form-wizard/dist/vue-form-wizard.min.css';

import WaitingListConfigurationTemplate
from '@/views/modules/waiting-list/WaitingListConfigurationTemplates.vue';
import WaitingListConfigurationWizardButtons
from '@/views/modules/waiting-list/WaitingListConfigurationWizardButtons.vue';
import UserAccountDetailsCompanyInformation from '@/views/modules/user/user-account/user-account-details/UserAccountDetailsCompanyInformation.vue';
import UserAccountBranding from '@/views/modules/user/user-account/user-account-branding/UserAccountBranding.vue';
import UserAccountCompanyPolicies from '@/views/modules/user/user-account/user-account-company-policies/UserAccountCompanyPolicies.vue';
import UserAccountOpeningHours from '@/views/modules/user/user-account/user-account-opening-hours/UserAccountOpeningHours.vue';

export default {
  name: 'WaitingListConfiguration',
  i18n: {
    messages: {
      en: {
        $Step1: {
          Title: 'Business details',
        },
        $Step2: {
          Title: 'Messaging',
        },
        $Step3: {
          Title: 'Branding',
        },
        $Step4: {
          Title: 'Company policies',
        },
        $Step5: {
          Title: 'Opening hours',
        },
      },
    },
  },
  components: {
    WizardButtons: WaitingListConfigurationWizardButtons,
    WaitingListConfigurationTemplate,
    UserAccountDetailsCompanyInformation,
    UserAccountBranding,
    UserAccountCompanyPolicies,
    UserAccountOpeningHours,
    FormWizard,
    TabContent,
  },
  props: {
    hideRequestReviewButton: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      tabIndex: 0,
      configTemplatesTabIndex: 0,
      configTemplatesKey: 0,
    };
  },
  computed: {
    ...mapGetters({
      tenantAccountSettingsWaitingList: 'auth/tenantAccountSettingsWaitingList',
      getTenantFlag: 'auth/getTenantFlag',
      isValidTenantAccountSettingsPrivacyPolicy: 'auth/isValidTenantAccountSettingsPrivacyPolicy',
    }),
    stickyWizardProps() {
      const $this = this;
      return {
        activeTabIndex: $this.tabIndex,
        isLastStep: $this.tabIndex === 4,
        nextTab() {
          if ($this.$refs.wizard && typeof $this.$refs.wizard.nextTab === 'function') {
            $this.$refs.wizard.nextTab();
          }
        },
        prevTab() {
          if ($this.$refs.wizard && typeof $this.$refs.wizard.prevTab === 'function') {
            $this.$refs.wizard.prevTab();
          }
        },
      };
    },
  },
  methods: {
    async validateAccountDetailsStep() {
      if (this.$refs.accountDetails.formIsChanged || !this.getTenantFlag('completedAccountDetails')) {
        return this.$refs.accountDetails.saveAccountDetails();
      }

      return true;
    },
    async validateTemplatesStep() {
      return this.$refs.configurationTemplate.saveTemplates();
    },
    async validateAccountBrandingStep() {
      if (this.$refs.accountBranding.formWasChanged) {
        await this.$refs.accountBranding.saveBranding();
      }

      return true;
    },
    async validateAccountCompanyPoliciesStep() {
      if (this.$refs.accountCompanyPolicies.formWasChanged) {
        return this.$refs.accountCompanyPolicies.savePolicy();
      }

      const formIsValid = await this.$refs.accountCompanyPolicies.$validator.validateAll();

      return formIsValid && this.isValidTenantAccountSettingsPrivacyPolicy;
    },
    async validateAccountOpeningHoursStep() {
      if (this.$refs.accountOpeningHours.formWasChanged) {
        await this.$refs.accountOpeningHours.saveOpeningHours();
      }

      this.$emit('saved');
      return true;
    },
    async saveAndClose() {
      let success = false;

      switch (this.tabIndex) {
        case 0:
          success = await this.validateAccountDetailsStep();
          break;
        case 1:
          success = await this.validateTemplatesStep();
          break;
        case 2:
          success = await this.validateAccountBrandingStep();
          break;
        case 3:
          success = await this.validateAccountCompanyPoliciesStep();
          break;
        case 4:
          success = await this.validateAccountOpeningHoursStep();
          break;
        default:
          success = true;
          break;
      }

      if (success && this.tabIndex !== 4) {
        this.$emit('saved');
      }
    },
    onChangeTab(prevIndex, nextIndex) {
      this.tabIndex = nextIndex;
      this.$refs.wizard.$el.scrollIntoView(true);
    },
  },
};
</script>

<style scoped lang="scss">

</style>
